'use client'
import { ItemImageCarousel } from '@/app/ui/parts/default/sp/item/itemImageCarousel'
import { ItemOptionsForMultiplePurchase } from '@/app/ui/parts/default/sp/item/itemOptionSelectForm'
import { Soldout } from '@/app/ui/parts/default/sp/item/soldout'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { AmazonPayConfig } from '@/lib/amazonpay/server'
import { singleItemResponseType } from '@/lib/item/server'
import { serviceType } from '@/lib/service/server'
import { bulkDiscountSettings } from '@/lib/service_type'
import { ChevronRight } from 'lucide-react'
import { Fragment } from 'react'

type ItemMainInfoProps = {
  item: singleItemResponseType
  service: serviceType
  liked: boolean
  amazonPayConfig: AmazonPayConfig | null
}

export const ItemMainInfoForMultiplePurchase: React.FC<ItemMainInfoProps> = ({
  service,
  item,
  liked,
  amazonPayConfig,
}) => {
  const bulkDiscountOption = service.service_options.find(
    (option) => option.option_type === 'bulk_discount',
  )?.settings as bulkDiscountSettings | undefined
  return (
    <Fragment>
      <ItemImageCarousel
        images={item.item_images}
        item_id={item.item.item_id}
        service_id={service.service_id}
        liked={liked}
        like_count={item.item.like_count}
        one_day_sale={item.item.one_day_sale}
        campaigns={item.campaigns}
        name={item.item.name}
      />
      <div className="item-main-info text-left m-4">
        <h1 className="text-base font-semibold text-[#424242]">
          {item.item.name}
        </h1>
        {item.item.discounted_price > 0 ? (
          <h3 className="sale-price text-xl font-semibold mb-4 mt-2 text-[#424242]">
            {item.item.discounted_price.toLocaleString()}{' '}
            <span className="text-sm font-light"> 円 (税込)</span>
            <span className="line-through text-xs text-gray-500 ml-2">
              {item.item.price.toLocaleString()}円 (割引前)
            </span>
          </h3>
        ) : (
          <h3 className="normal-price text-xl font-bold my-2 text-[#424242]">
            {item.item.price.toLocaleString()}{' '}
            <span className="text-sm font-light"> 円 (税込)</span>
          </h3>
        )}
      </div>
      {!item.item.stock ? (
        <Soldout />
      ) : (
        <>
          {service.service_id === 'pairl' ? (
            <Fragment>
              <div className="px-4 py-2">
                <Dialog>
                  <DialogTrigger className="select-size-button button-color main-color text-white w-full text-base rounded-lg text-center relative font-bold p-4 items-center">
                    <p>
                      2人分のサイズ・カラーを選ぶ
                      <span
                        className="absolute font-bold text-2xl text-white"
                        style={{ top: '18px', right: '12px' }}
                      >
                        <ChevronRight
                          strokeWidth={'3px'}
                          width={20}
                          height={20}
                        />
                      </span>
                    </p>
                  </DialogTrigger>
                  <DialogContent className="p-[12px] bg-white w-full rounded-md w-[90%] mx-auto">
                    <DialogHeader className="text-left">
                      <DialogTitle className="text-sm font-normal">
                        {bulkDiscountOption
                          ? `${bulkDiscountOption.min_quantity}点以上購入で` +
                            (bulkDiscountOption.discount_type === 'percentage'
                              ? ` ${bulkDiscountOption.discount_rate}%OFF `
                              : `${bulkDiscountOption.discount_rate}円割引`) +
                            'が適用されます。'
                          : '商品のオプションを選択してください。'}
                      </DialogTitle>
                    </DialogHeader>
                    <ItemOptionsForMultiplePurchase
                      service={service}
                      item={item.item}
                      category={item.category}
                      itemOptions={item.item_options}
                      defaultItemCount={2}
                      variant="buyAndAddCart"
                      modal={true}
                      promotionModal={false}
                      amazonPayConfig={amazonPayConfig}
                    />
                  </DialogContent>
                </Dialog>
              </div>
              <div className="px-4 py-2 text-center">
                <Dialog>
                  <DialogTrigger className="select-size-button-2 main-color text-white w-[84%] text-base rounded-lg text-center relative font-bold p-4 items-center">
                    <p>
                      1人分のサイズ・カラーを選ぶ
                      <span
                        className="absolute font-bold text-2xl text-white"
                        style={{ top: '18px', right: '12px' }}
                      >
                        <ChevronRight
                          strokeWidth={'3px'}
                          width={20}
                          height={20}
                        />
                      </span>
                    </p>
                  </DialogTrigger>
                  <DialogContent className="p-[12px] bg-white w-full rounded-md w-[90%] mx-auto">
                    <DialogHeader className="text-left">
                      <DialogTitle className="text-sm font-normal">
                        {bulkDiscountOption
                          ? `${bulkDiscountOption.min_quantity}点以上購入で` +
                            (bulkDiscountOption.discount_type === 'percentage'
                              ? ` ${bulkDiscountOption.discount_rate}%OFF `
                              : `${bulkDiscountOption.discount_rate}円割引`) +
                            'が適用されます。'
                          : '商品のオプションを選択してください。'}
                      </DialogTitle>
                    </DialogHeader>
                    <ItemOptionsForMultiplePurchase
                      service={service}
                      item={item.item}
                      category={item.category}
                      itemOptions={item.item_options}
                      defaultItemCount={1}
                      variant="buyAndAddCart"
                      modal={true}
                      promotionModal={false}
                      amazonPayConfig={amazonPayConfig}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </Fragment>
          ) : (
            <div className="px-4 py-2 text-center">
              <Dialog>
                <DialogTrigger className="select-size-button button-color main-color text-white w-full text-base rounded-lg text-center relative font-bold p-4 items-center">
                  <p>
                    サイズ・カラーを選ぶ
                    <span
                      className="absolute font-bold text-2xl text-white"
                      style={{ top: '18px', right: '12px' }}
                    >
                      <ChevronRight
                        strokeWidth={'3px'}
                        width={20}
                        height={20}
                      />
                    </span>
                  </p>
                </DialogTrigger>
                <DialogContent className="p-[12px] bg-white w-full rounded-md w-[90%] mx-auto">
                  <DialogHeader className="text-left">
                    <DialogTitle className="text-sm font-normal">
                      {bulkDiscountOption
                        ? `${bulkDiscountOption.min_quantity}点以上購入で` +
                          (bulkDiscountOption.discount_type === 'percentage'
                            ? ` ${bulkDiscountOption.discount_rate}%OFF `
                            : `${bulkDiscountOption.discount_rate}円割引`) +
                          'が適用されます。'
                        : '商品のオプションを選択してください。'}
                    </DialogTitle>
                  </DialogHeader>
                  <ItemOptionsForMultiplePurchase
                    service={service}
                    item={item.item}
                    category={item.category}
                    itemOptions={item.item_options}
                    defaultItemCount={1}
                    variant="buyAndAddCart"
                    modal={true}
                    promotionModal={false}
                    amazonPayConfig={amazonPayConfig}
                  />
                </DialogContent>
              </Dialog>
            </div>
          )}
        </>
      )}
      <div
        className="fixed bottom-0 left-0 text-center w-full bg-white p-4"
        style={{
          zIndex: 30,
          boxShadow:
            '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
      >
        {!item.item.stock ? (
          <Soldout />
        ) : (
          <Dialog>
            <DialogTrigger className="select-size-button button-color main-color text-white w-full text-base rounded-lg text-center relative font-bold p-4 items-center">
              <p>
                サイズ・カラーを選ぶ
                <span
                  className="absolute font-bold text-2xl text-white"
                  style={{ top: '18px', right: '12px' }}
                >
                  <ChevronRight strokeWidth={'3px'} width={20} height={20} />
                </span>
              </p>
            </DialogTrigger>
            <DialogContent className="p-[12px] bg-white w-full rounded-md w-[90%] mx-auto">
              <DialogHeader className="text-left">
                <DialogTitle className="text-sm font-normal">
                  商品のオプションを選択してください。
                </DialogTitle>
              </DialogHeader>
              <ItemOptionsForMultiplePurchase
                service={service}
                item={item.item}
                category={item.category}
                itemOptions={item.item_options}
                defaultItemCount={service.service_id === 'pairl' ? 2 : 1}
                variant="buyAndAddCart"
                modal={true}
                promotionModal={false}
                amazonPayConfig={amazonPayConfig}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Fragment>
  )
}
